import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent
} from "react";
import type { EChartsOption } from "echarts";
import type { LineSeriesOption } from "echarts/charts";
import type { EChartsType } from "echarts/core";
import type { MetricSeries, RunProjection } from "@roboscientist/contracts";
import { progressStageValue, progressUnit, progressUnitLabel, progressValue } from "./runPresentation.js";
import { EmptyState, OverflowReveal, StatusPill } from "@roboscientist/ui";
import { useThemePalette } from "../useThemePalette.js";
import { Chart } from "./Chart.js";
import {
  TENSORBOARD_SMOOTHING_MAX,
  telemetrySeriesColor,
  tensorboardSmooth
} from "./chartMath.js";
import { telemetryState } from "./TelemetryRunSelector.js";

type MetricTooltipParam = {
  axisValue?: number | string;
  color?: string;
  data?: unknown;
  seriesId?: string;
  seriesName?: string;
  value?: unknown;
};
type MetricZoomWindow = { start: number; end: number };
type MetricZoomGesture = {
  pointerId: number;
  mode: "zoom" | "pan";
  startX: number;
  currentX: number;
  window: MetricZoomWindow;
  moved: boolean;
};
type MetricZoomSelection = { x0: number; x1: number };

const METRIC_GRID_LEFT = 48;
const METRIC_GRID_RIGHT = 20;

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (character) => ({
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  "\"": "&quot;",
  "'": "&#39;"
})[character]!);

const metricValue = (param: MetricTooltipParam): number | null => {
  const source = Array.isArray(param.value) ? param.value : Array.isArray(param.data) ? param.data : null;
  const value = source?.[1];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

const formatMetricValue = (value: number | null) => value == null
  ? "—"
  : value.toLocaleString(undefined, { maximumSignificantDigits: 7 });

const metricZoomWindow = (instance: EChartsType): MetricZoomWindow => {
  const option = instance.getOption() as { dataZoom?: Array<{ start?: number; end?: number }> };
  const zoom = option.dataZoom?.[0];
  return {
    start: typeof zoom?.start === "number" ? zoom.start : 0,
    end: typeof zoom?.end === "number" ? zoom.end : 100
  };
};

const formatMetricTooltip = (params: unknown, unit: string, fallbackColor: string): string => {
  const list = (Array.isArray(params) ? params : [params]).filter(Boolean) as MetricTooltipParam[];
  if (!list.length) return "";
  const rows = new Map<string, { color: string; raw: number | null; smoothed: number | null; value: number | null }>();
  for (const param of list) {
    const name = param.seriesName ?? "metric";
    const row = rows.get(name) ?? { color: param.color ?? fallbackColor, raw: null, smoothed: null, value: null };
    const value = metricValue(param);
    if (param.seriesId?.startsWith("raw:")) row.raw = value;
    else if (param.seriesId?.startsWith("smooth:")) row.smoothed = value;
    else row.value = value;
    rows.set(name, row);
  }
  const step = escapeHtml(String(list[0]?.axisValue ?? "—"));
  const body = [...rows.entries()].map(([name, row]) => {
    const details = row.smoothed == null
      ? formatMetricValue(row.value ?? row.raw)
      : `<strong>${formatMetricValue(row.smoothed)}</strong><small>raw ${formatMetricValue(row.raw)}</small>`;
    return `<div class="metric-tooltip-row"><i style="background:${row.color}"></i><span>${escapeHtml(name)}</span>${details}</div>`;
  }).join("");
  return `<div class="metric-tooltip"><header>${escapeHtml(unit)} ${step}</header>${body}</div>`;
};

export type TrainingReplayPresentation = {
  series: MetricSeries[];
  active: boolean;
  playing: boolean;
  onInspect: () => void;
};

export function TrainingChart({ run, series, compact = false, replay }: { run: RunProjection | null; series: MetricSeries[]; compact?: boolean; replay?: TrainingReplayPresentation }) {
  const { isLight, palette } = useThemePalette();
  const [smoothing, setSmoothing] = useState(0);
  const [zoomWindow, setZoomWindow] = useState<MetricZoomWindow>({ start: 0, end: 100 });
  const [zoomSelection, setZoomSelection] = useState<MetricZoomSelection | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const chartInstanceRef = useRef<EChartsType | null>(null);
  const chartInteractionRef = useRef<HTMLDivElement | null>(null);
  const zoomGestureRef = useRef<MetricZoomGesture | null>(null);
  const chartText = palette.muted;
  const seriesColors = {
    accent: palette.accent,
    attention: palette.attention,
    cyan: palette.ansi.cyan,
    danger: palette.danger,
    done: palette.done,
    success: palette.success
  };
  const domainSeries = (replay?.series ?? series).filter((entry) => entry.points.length && entry.name !== "elapsed_seconds" && entry.name !== "eta_seconds");
  const plotted = replay ? domainSeries.map((entry) => series.find((current) => current.name === entry.name) ?? { ...entry, points: [] }) : domainSeries;
  useEffect(() => {
    const element = chartInteractionRef.current;
    if (!element) return;
    const handleTrackpadPan = (event: WheelEvent) => {
      const horizontalDelta = event.deltaX;
      const horizontalGesture = Math.abs(horizontalDelta) > 0 && Math.abs(horizontalDelta) > Math.abs(event.deltaY);
      if (!event.ctrlKey && !horizontalGesture) return;

      // Use a native non-passive listener so the browser cannot turn a
      // horizontal gesture at the chart boundary into history navigation.
      event.preventDefault();
      event.stopPropagation();
      if (event.ctrlKey || !horizontalGesture) return;

      const instance = chartInstanceRef.current;
      if (!instance) return;
      const current = metricZoomWindow(instance);
      const span = current.end - current.start;
      if (span >= 99.9999) return;

      const width = Math.max(1, element.getBoundingClientRect().width);
      const deltaInPixels = event.deltaMode === 1
        ? horizontalDelta * 16
        : event.deltaMode === 2
          ? horizontalDelta * width
          : horizontalDelta;
      const requestedShift = Math.max(-span * 0.25, Math.min(span * 0.25, deltaInPixels / width * span));
      const nextStart = Math.max(0, Math.min(100 - span, current.start + requestedShift));
      instance.dispatchAction({ type: "dataZoom", start: nextStart, end: nextStart + span });
    };
    element.addEventListener("wheel", handleTrackpadPan, { capture: true, passive: false });
    return () => element.removeEventListener("wheel", handleTrackpadPan, true);
  }, [compact, plotted.length, run?.id]);
  if (!run) return <EmptyState title="No active Run metrics" detail="Select a Run from the experiment timeline or curve list to inspect its collected metrics." />;
  const axisUnit = progressUnit(run);
  const stage = progressStageValue(run);

  const smoothingEnabled = smoothing > 0;
  const chartSeries: LineSeriesOption[] = plotted.flatMap((entry, index): LineSeriesOption[] => {
    const color = telemetrySeriesColor(entry.name, index, isLight, seriesColors);
    const points = [...entry.points].sort((a, b) => a.step - b.step);
    const rawData = points.map((point) => [point.step, point.value]);
    if (!smoothingEnabled) {
      return [{
        id: `value:${entry.name}`,
        name: entry.name,
        type: "line" as const,
        showSymbol: false,
        sampling: "lttb" as const,
        connectNulls: false,
        lineStyle: { color, width: entry.name === "action_loss" ? 2 : 1.4, opacity: entry.name === "action_loss" ? 1 : 0.78 },
        itemStyle: { color },
        emphasis: { disabled: true },
        data: rawData
      }];
    }

    const smoothed = tensorboardSmooth(points.map((point) => point.value), smoothing);
    return [{
      id: `smooth:${entry.name}`,
      name: entry.name,
      type: "line" as const,
      z: 2,
      showSymbol: false,
      sampling: "lttb" as const,
      connectNulls: false,
      lineStyle: { color, width: entry.name === "action_loss" ? 2.2 : 1.65, opacity: 1 },
      itemStyle: { color },
      emphasis: { disabled: true },
      data: points.map((point, pointIndex) => [point.step, smoothed[pointIndex] ?? point.value])
    }, {
      id: `raw:${entry.name}`,
      name: entry.name,
      type: "line" as const,
      z: 1,
      showSymbol: false,
      sampling: "lttb" as const,
      connectNulls: false,
      lineStyle: { color, width: 1, opacity: 0.32 },
      itemStyle: { color, opacity: 0.32 },
      emphasis: { disabled: true },
      data: rawData
    }];
  });

  const domain = (replay ? domainSeries : []).flatMap((entry) => entry.points).reduce((bounds, point) => ({
    maxStep: Math.max(bounds.maxStep, point.step), minValue: Math.min(bounds.minValue, point.value), maxValue: Math.max(bounds.maxValue, point.value)
  }), { maxStep: 1, minValue: Infinity, maxValue: -Infinity });
  const padding = Math.max((domain.maxValue - domain.minValue) * .08, Math.abs(domain.maxValue) * .02, .0001);
  const fixedAxes = replay && Number.isFinite(domain.minValue);
  const option: EChartsOption = {
    animation: !replay,
    textStyle: { fontFamily: "Inter, sans-serif" },
    animationDuration: 260,
    animationDurationUpdate: 180,
    color: plotted.map((entry, index) => telemetrySeriesColor(entry.name, index, isLight, seriesColors)),
    grid: { left: 48, right: 20, top: 38, bottom: 34 },
    legend: {
      data: plotted.map((entry, index) => {
        const color = telemetrySeriesColor(entry.name, index, isLight, seriesColors);
        return { name: entry.name, itemStyle: { color, opacity: 1 }, lineStyle: { color, opacity: 1, width: 2 } };
      }),
      left: 0,
      right: 0,
      textStyle: { color: chartText, fontSize: 11 },
      top: 2
    },
    dataZoom: [{
      type: "inside",
      xAxisIndex: 0,
      filterMode: "filter",
      zoomOnMouseWheel: false,
      moveOnMouseMove: false,
      moveOnMouseWheel: false,
      preventDefaultMouseMove: false
    }],
    tooltip: {
      trigger: "axis",
      transitionDuration: 0,
      axisPointer: { type: "line", snap: false },
      backgroundColor: palette.canvasOverlay,
      borderColor: palette.border,
      textStyle: { color: palette.foreground },
      formatter: (params: unknown) => formatMetricTooltip(params, axisUnit, palette.muted)
    },
    xAxis: { type: "value", min: fixedAxes ? 0 : undefined, max: fixedAxes ? domain.maxStep : undefined, name: axisUnit, nameTextStyle: { color: chartText }, axisLabel: { color: chartText }, splitLine: { show: false } },
    yAxis: { type: "value", min: fixedAxes ? domain.minValue - padding : undefined, max: fixedAxes ? domain.maxValue + padding : undefined, scale: true, axisLabel: { color: chartText, formatter: (value: number) => value.toLocaleString(undefined, { maximumSignificantDigits: 4 }) }, splitLine: { lineStyle: { color: palette.borderMuted } } },
    series: chartSeries
  };

  const syncZoomWindow = (instance: EChartsType) => {
    const zoom = metricZoomWindow(instance);
    setZoomWindow((current) =>
      Math.abs(current.start - zoom.start) < 0.0001 && Math.abs(current.end - zoom.end) < 0.0001
        ? current
        : zoom
    );
  };
  const prepareChartInteractions = (instance: EChartsType) => {
    chartInstanceRef.current = instance;
    syncZoomWindow(instance);
    instance.on("datazoom", () => syncZoomWindow(instance));
  };
  const metricX = (clientX: number, element: HTMLDivElement) => {
    const bounds = element.getBoundingClientRect();
    return Math.max(METRIC_GRID_LEFT, Math.min(bounds.width - METRIC_GRID_RIGHT, clientX - bounds.left));
  };
  const handleMetricPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 && event.button !== 2) return;
    const startX = metricX(event.clientX, event.currentTarget);
    const mode = event.button === 2 ? "pan" : "zoom";
    const instance = chartInstanceRef.current;
    zoomGestureRef.current = {
      pointerId: event.pointerId,
      mode,
      startX,
      currentX: startX,
      window: instance ? metricZoomWindow(instance) : zoomWindow,
      moved: false
    };
    setZoomSelection(null);
    if (mode === "pan") {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsPanning(true);
    }
  };
  const handleMetricPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = zoomGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    const currentX = metricX(event.clientX, event.currentTarget);
    if (Math.abs(currentX - gesture.startX) < 3 && !gesture.moved) return;
    if (!gesture.moved) event.currentTarget.setPointerCapture(event.pointerId);
    gesture.moved = true;
    gesture.currentX = currentX;
    event.preventDefault();
    if (gesture.mode === "pan") {
      const instance = chartInstanceRef.current;
      if (!instance) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      const plotWidth = Math.max(1, bounds.width - METRIC_GRID_LEFT - METRIC_GRID_RIGHT);
      const span = gesture.window.end - gesture.window.start;
      const requestedStart = gesture.window.start - (currentX - gesture.startX) / plotWidth * span;
      const nextStart = Math.max(0, Math.min(100 - span, requestedStart));
      instance.dispatchAction({ type: "dataZoom", start: nextStart, end: nextStart + span });
      setZoomSelection(null);
      return;
    }
    setZoomSelection({ x0: Math.min(gesture.startX, currentX), x1: Math.max(gesture.startX, currentX) });
  };
  const finishMetricZoom = (event: ReactPointerEvent<HTMLDivElement>, applySelection = true) => {
    const gesture = zoomGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    const instance = chartInstanceRef.current;
    const x0 = Math.min(gesture.startX, gesture.currentX);
    const x1 = Math.max(gesture.startX, gesture.currentX);
    if (instance && gesture.mode === "zoom" && applySelection && gesture.moved && x1 - x0 >= 8) {
      const bounds = event.currentTarget.getBoundingClientRect();
      const plotWidth = Math.max(1, bounds.width - METRIC_GRID_LEFT - METRIC_GRID_RIGHT);
      const current = metricZoomWindow(instance);
      const span = current.end - current.start;
      const nextStart = current.start + (x0 - METRIC_GRID_LEFT) / plotWidth * span;
      const nextEnd = current.start + (x1 - METRIC_GRID_LEFT) / plotWidth * span;
      instance.dispatchAction({ type: "dataZoom", start: nextStart, end: nextEnd });
    }
    zoomGestureRef.current = null;
    setIsPanning(false);
    setZoomSelection(null);
  };
  const resetMetricZoom = () => {
    const instance = chartInstanceRef.current;
    if (!instance) return;
    instance.dispatchAction({ type: "dataZoom", start: 0, end: 100 });
    setZoomSelection(null);
  };
  return <div className={`training-module${replay?.active ? " is-replay-active" : ""}`} onPointerDownCapture={replay?.onInspect} onKeyDownCapture={replay?.onInspect}>
    <div className="run-strip">
      <div><span className="kicker">{run.kind.toUpperCase()}</span><OverflowReveal as="strong" value={run.id} displayValue={run.id.split(":").at(-1) ?? run.id} kind="identifier" label="Run ID" copyable focusable /></div>
      <StatusPill tone={replay?.active ? "live" : run.status === "active" ? "live" : run.status === "completed" ? "accent" : "warning"}>{replay?.active ? "Replaying saved Run" : run.status}</StatusPill>
    </div>
    <div className="run-stats">
      <div><span>{progressUnitLabel(run)}</span><strong>{progressValue(run)}</strong></div>
      {stage ? <div><span>STAGE</span><strong>{stage}</strong></div> : null}
      <div><span>{run.primaryMetricName ?? "METRIC"}</span><strong>{run.primaryMetricValue?.toFixed(4) ?? "—"}</strong></div>
      <div><span>ETA</span><strong>{run.etaSeconds == null ? "—" : `${Math.round(run.etaSeconds)}s`}</strong></div>
    </div>
    {plotted.length ? <>
      <div className="training-chart-controls">
        <label title="TensorBoard-style exponentially weighted smoothing. Set to zero to show only raw values.">
          <span>Smoothing</span>
          <input
            aria-label="Metric smoothing"
            type="range"
            min="0"
            max={TENSORBOARD_SMOOTHING_MAX}
            step="0.001"
            value={smoothing}
            onChange={(event) => setSmoothing(Number(event.target.value))}
          />
          <output>{smoothing.toFixed(3)}</output>
        </label>
        <small>Drag to zoom · swipe/right-drag to pan · double-click to reset</small>
      </div>
      <div
        ref={chartInteractionRef}
        className={`metric-chart-interaction${isPanning ? " is-panning" : ""}`}
        data-zoom-start={zoomWindow.start}
        data-zoom-end={zoomWindow.end}
        onPointerDown={handleMetricPointerDown}
        onPointerMove={handleMetricPointerMove}
        onPointerUp={finishMetricZoom}
        onPointerCancel={(event) => finishMetricZoom(event, false)}
        onContextMenu={(event) => event.preventDefault()}
        onDoubleClick={(event) => { event.preventDefault(); resetMetricZoom(); }}
      >
        <Chart key={`${run.id}:${compact ? "compact" : "full"}`} option={option} style={{ height: compact ? 250 : 390 }} replaceMerge={["series"]} onChartReady={prepareChartInteractions} />
        {zoomSelection ? <div className="metric-zoom-selection" aria-hidden="true" style={{ "--selection-x": `${zoomSelection.x0}px`, "--selection-width": `${zoomSelection.x1 - zoomSelection.x0}px` } as CSSProperties} /> : null}
      </div>
    </> : <EmptyState title={telemetryState(run)} detail={run.capabilities.metricsDeclared ? "The Run declared telemetry, but no numeric metric row is available yet." : "This Run did not declare a telemetry output."} />}
  </div>;
}
